import { useMemo, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useDataLoader } from "../hooks/useDataLoader";
import { loadCricketData } from "../utils/dataLoader";
import type { CricketWinner } from "../types/data";
import { SEO } from "../components/SEO";
import { routeSeo } from "../content/seoRoutes";

export function CricketPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || "worldcup";
  const selectedFormat = searchParams.get('format') || "All";

  const { data, loading, error } = useDataLoader(loadCricketData, []);
  const worldCupWinners: CricketWinner[] = data?.worldCupWinners ?? [];
  const iplWinners: CricketWinner[] = data?.iplWinners ?? [];

  const handleTabChange = useCallback((tab: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (tab && tab !== 'worldcup') next.set('tab', tab);
      else next.delete('tab');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const handleFormatChange = useCallback((format: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (format && format !== 'All') next.set('format', format);
      else next.delete('format');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const filteredIccWinners = useMemo(() => {
    if (selectedFormat === "All") return worldCupWinners;
    return worldCupWinners.filter((winner) => winner.tournament === selectedFormat);
  }, [worldCupWinners, selectedFormat]);

  const getBadgeColor = (tournament: string) => {
    switch (tournament) {
      case "ODI World Cup":
        return "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800";
      case "T20 World Cup":
        return "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800";
      case "World Test Championship":
        return "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800";
      case "Champions Trophy":
        return "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-750";
    }
  };

  // SEO structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Dataset",
    "name": "Cricket Winners and Tournaments Database",
    "description": "Comprehensive database of winners, runners-up, venues, and years for major cricket tournaments including IPL and ICC events (ODI World Cup, T20 World Cup, WTC)",
    "keywords": ["cricket winners", "IPL winners list", "ICC world cup winners", "cricket tournament records"],
    "license": "https://creativecommons.org/licenses/by/4.0/",
    "creator": {
      "@type": "Organization",
      "name": "Indian Codes Hub"
    },
    "distribution": {
      "@type": "DataDownload",
      "contentUrl": `${window.location.origin}/cricket`,
      "encodingFormat": "text/html"
    }
  };

  return (
    <>
      <SEO
        {...routeSeo('cricket')}
        keywords="cricket winners, IPL winners list, ICC world cup winners, cricket tournament records, IPL history, ICC champions trophy"
        structuredData={structuredData}
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="text-center mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Cricket Winners</h1>
        <p className="text-sm sm:text-base text-gray-600">History of IPL and ICC tournament winners</p>
      </div>

      {loading && (
        <div className="text-center py-12 text-gray-500">Loading cricket data...</div>
      )}
      {error && (
        <div className="text-center py-12 text-red-500">{error}</div>
      )}

      {!loading && !error && (
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="worldcup">ICC Tournaments</TabsTrigger>
            <TabsTrigger value="ipl">IPL Winners</TabsTrigger>
          </TabsList>

          <TabsContent value="worldcup">
            {/* Format Selector Pills */}
            <div className="flex flex-wrap gap-2 mb-6">
              {["All", "ODI World Cup", "T20 World Cup", "World Test Championship", "Champions Trophy"].map((format) => (
                <button
                  key={format}
                  onClick={() => handleFormatChange(format)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-300 border ${
                    selectedFormat === format
                      ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-transparent shadow-sm"
                      : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                  }`}
                >
                  {format}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredIccWinners.map((winner, idx) => (
                <Card key={`${winner.year}-${winner.tournament}-${idx}`} className="hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-xl font-bold text-blue-600">{winner.year}</span>
                        {winner.winner === "TBD" && (
                          <Badge className={
                            winner.year === 2026
                              ? "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300"
                              : "bg-sky-100 text-sky-800 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300"
                          }>
                            {winner.year === 2026 ? "Ongoing" : "Upcoming"}
                          </Badge>
                        )}
                      </div>
                      <Badge variant="outline" className={getBadgeColor(winner.tournament)}>
                        {winner.tournament}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Winner</p>
                        {winner.winner === "TBD" ? (
                          <p className="font-semibold text-amber-600 dark:text-amber-400">To Be Decided</p>
                        ) : (
                          <p className="font-semibold text-green-600">{winner.winner}</p>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Runner-up</p>
                        {winner.winner === "TBD" ? (
                          <p className="font-medium text-gray-400 dark:text-gray-500">To Be Decided</p>
                        ) : (
                          <p className="font-medium text-gray-700">{winner.runnerUp}</p>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Venue / Host</p>
                        <p className="text-sm text-gray-600">{winner.venue}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {filteredIccWinners.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">No tournaments found for this category.</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="ipl">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {iplWinners.map((winner) => (
                <Card key={`${winner.year}-${winner.tournament}`} className="hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-xl font-bold text-orange-600">{winner.year}</span>
                        {winner.winner === "TBD" && (
                          <Badge className={
                            winner.year === 2026
                              ? "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300"
                              : "bg-sky-100 text-sky-800 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300"
                          }>
                            {winner.year === 2026 ? "Ongoing" : "Upcoming"}
                          </Badge>
                        )}
                      </div>
                      <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950 dark:text-orange-300 dark:border-orange-800">
                        {winner.tournament}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Winner</p>
                        {winner.winner === "TBD" ? (
                          <p className="font-semibold text-amber-600 dark:text-amber-400">To Be Decided</p>
                        ) : (
                          <p className="font-semibold text-green-600">{winner.winner}</p>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Runner-up</p>
                        {winner.winner === "TBD" ? (
                          <p className="font-medium text-gray-400 dark:text-gray-500">To Be Decided</p>
                        ) : (
                          <p className="font-medium text-gray-700">{winner.runnerUp}</p>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Venue</p>
                        <p className="text-sm text-gray-600">{winner.venue}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
    </>
  );
}